import { forwardRef, useState } from 'react';

import produce from 'immer';
import styled from 'styled-components';

import { Button, ButtonProps } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { EmptyState } from '@components/empty-state';
import {
  ConditionComparator,
  ConditionOp,
  FieldId,
  FormFieldCondition,
  FormFieldConditionForm,
  FormFieldConditionJourney,
  FormFieldValue,
  isFormSourceCondition,
} from '@components/form/v2';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconCondition } from '@components/icons/condition';
import { IconDropdown } from '@components/icons/dropdown';
import { IconElement } from '@components/icons/element';
import { IconForm } from '@components/icons/form';
import { IconMappingOn } from '@components/icons/mapping-on';
import { Menu } from '@components/menu';
import {
  MultiSegmentedFilters,
  SegmentConfig,
} from '@components/multi-segmented-filters/multi-segmented-filters';
import { Select, SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getFieldContent } from '@components/text-editors/addons/field-mapping';
import { getScopedTranslate, styledTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { randomId } from '@utils/strings';
import { isValidDateString } from '@utils/validators';

import {
  applyComparatorChange,
  applyValueChange,
  buildResetFormCondition,
  buildResetJourneyCondition,
  canSaveCondition,
  ConditionDraftForm,
  getComparatorsForFieldType,
  getRenderableConditionCount,
  isCompleteCondition,
  isMultiComparator,
  journeyVariableGroupsAreNonEmpty,
} from './condition-helpers';
import { useFormBuilderContext } from './context';
import { getFieldConfigForKind } from './field-registry';
import { JourneyConditionRow } from './journey-condition-row';
import { StateField, StateFieldWithCondition } from './state';
import {
  filterGroupsToConditionVariables,
  useJourneyVariablePicker,
} from './use-journey-variable-picker';
import { comparatorColor, ComparatorMenuItems } from './variable-condition-menus';

const MENU_WIDTH = pxToRem(308);

const filterableKinds = [
  'input-date',
  'input-dropdown',
  'input-multiple-choice',
  'input-single-choice',
] as const;

type FilterableKind = (typeof filterableKinds)[number];

type FilterableQuestion = Extract<StateField, { kind: FilterableKind }>;

const isFilterableQuestion = (question: StateField): question is FilterableQuestion =>
  filterableKinds.includes(question.kind as FilterableKind);

const QuestionLabel = ({ id, onUnselect }: { id: FieldId; onUnselect(): void }) => {
  const {
    store: { useFormFieldFromId },
  } = useFormBuilderContext();
  const field = useFormFieldFromId<Extract<StateField, { label: any }>>(id);
  const label = getFieldContent(field.label);

  return (
    <Menu.Item
      selectionType="open"
      isHeader
      label={label.toUpperCase()}
      icon={<IconArrowLeft />}
      onSelect={onUnselect}
    />
  );
};

const useFilterableQuestions = (currentId: FieldId) => {
  const {
    store: { useFlattenedList },
  } = useFormBuilderContext();

  const flattened = useFlattenedList(true);
  const currentIndex = flattened.findIndex((field) => field.id === currentId);
  const flattenedBeforeMe = flattened.slice(0, currentIndex);

  return flattenedBeforeMe.filter(isFilterableQuestion);
};

type ConditionSource = 'form' | 'journey';

const SourcePicker = ({ onSelect }: { onSelect(source: ConditionSource): void }) => {
  const { t } = useScopedTranslate(conditionsLocale);
  return (
    <>
      <SourcePickerHeader>{t('sourceHeader')}</SourcePickerHeader>
      <Menu.Item
        label={t('sourceForm')}
        helpText={t('sourceFormDescription')}
        icon={<IconForm />}
        selectionType="open"
        onSelect={() => onSelect('form')}
      />
      <Menu.Item
        label={t('sourceJourney')}
        helpText={t('sourceJourneyDescription')}
        icon={<IconMappingOn color="elementBlue100" />}
        selectionType="open"
        onSelect={() => onSelect('journey')}
      />
    </>
  );
};

const QuestionMenu = ({
  onClose,
  onBack,
  field,
  onSave,
}: {
  onClose(): void;
  onBack?(): void;
  field: StateFieldWithCondition;
  onSave(condition: FormFieldConditionForm): void;
}) => {
  const [state, setState] = useState<ConditionDraftForm>({ source: 'form', id: randomId() });
  const { t } = useScopedTranslate(conditionsLocale);
  const filterableQuestions = useFilterableQuestions(field.id);

  if (!state.questionId) {
    return (
      <>
        {onBack && (
          <>
            <Menu.Item
              isHeader
              label={t('sourceForm')}
              icon={<IconArrowLeft />}
              selectionType="open"
              onSelect={onBack}
            />
            <Menu.Separator />
          </>
        )}
        {filterableQuestions.length === 0 ? (
          <EmptyConditionCard
            header={t('emptyHeader')}
            content={t('emptyContent')}
            icon={<IconElement size={48} />}
          />
        ) : (
          filterableQuestions.map((question) => {
            const Icon = getFieldConfigForKind(question.kind).Icon;
            return (
              <Menu.Item
                key={question.id}
                label={question.label}
                icon={<Icon />}
                selectionType="open"
                onSelect={() => {
                  setState((old) => ({ ...old, questionId: question.id }));
                }}
              />
            );
          })
        )}
      </>
    );
  }

  const onValueSelect = (value: string) => {
    if (!state.comparator) return;
    const isMulti = isMultiComparator(state.comparator);

    setState(
      produce(state, (draft) => {
        if (!isMulti) {
          draft.value = value;
          return;
        }

        if (!value) return;

        const currentValue = (draft.value ?? []) as string[];
        if (currentValue.includes(value)) {
          draft.value = currentValue.filter((v) => v !== value);
        } else {
          draft.value = [...currentValue, value];
        }
      })
    );
  };

  return (
    <>
      <QuestionLabel
        id={state.questionId}
        onUnselect={() => {
          setState((old) => ({ source: 'form', id: old.id }));
        }}
      />

      {state.comparator && (
        <Menu.Item
          selectionType="open"
          isHeader
          label={t(`core.comparator.${state.comparator}`).replace('...', '')}
          icon={<IconArrowLeft />}
          onSelect={() => {
            setState((old) => ({
              source: 'form',
              id: old.id,
              questionId: old.questionId,
            }));
          }}
        />
      )}

      <Menu.Separator />

      {!state.comparator && (
        <ComparatorMenu
          questionId={state.questionId}
          onSelect={(comparator) => {
            setState((old) => ({ ...old, comparator }));
          }}
        />
      )}

      {state.comparator && (
        <>
          <FieldValueMenu
            questionId={state.questionId}
            comparator={state.comparator}
            value={state.value}
            onSelect={onValueSelect}
          />

          <Menu.Separator />

          <ButtonGroup align="right">
            <Button
              isDisabled={!canSaveCondition(state)}
              variant="primary"
              text={t('core.save')}
              onClick={() => {
                onSave({
                  source: 'form',
                  id: state.id,
                  questionId: state.questionId as string,
                  comparator: state.comparator as ConditionComparator,
                  value: state.value as FormFieldValue,
                });
                onClose();
              }}
            />
          </ButtonGroup>
        </>
      )}
    </>
  );
};

const ElementPickerMenu = ({
  field,
  onSelect,
}: {
  field: StateFieldWithCondition;
  onSelect(questionId: FieldId): void;
}) => {
  const filterableQuestions = useFilterableQuestions(field.id);
  return (
    <>
      {filterableQuestions.map((question) => {
        const Icon = getFieldConfigForKind(question.kind).Icon;
        return (
          <Menu.Item
            key={question.id}
            label={question.label}
            icon={<Icon />}
            selectionType="open"
            onSelect={() => onSelect(question.id)}
          />
        );
      })}
    </>
  );
};

const ComparatorMenu = ({
  questionId,
  onSelect,
}: {
  questionId: FieldId;
  onSelect(comparator: ConditionComparator): void;
}) => {
  const {
    store: { useFormFieldFromId },
  } = useFormBuilderContext();
  const field = useFormFieldFromId(questionId);
  const config = getFieldConfigForKind(field.kind);

  if (!config.condition) return null;

  return <ComparatorMenuItems comparators={config.condition.comparators} onSelect={onSelect} />;
};

const FieldValueMenu = ({
  questionId,
  comparator,
  value,
  onSelect,
}: {
  questionId: FieldId;
  comparator: ConditionComparator;
  value?: FormFieldValue;
  onSelect(value: FormFieldValue): void;
}) => {
  const {
    store: { useFormFieldFromId },
  } = useFormBuilderContext();
  const field = useFormFieldFromId(questionId);
  const config = getFieldConfigForKind(field.kind);

  if (!config.condition) return null;

  return (
    <config.condition.ValueMenu
      field={field}
      comparator={comparator}
      value={value}
      onSelect={onSelect}
    />
  );
};

const useFormSourceDisplay = (condition: FormFieldConditionForm) => {
  const {
    predefinedOptionsLists,
    store: { getState },
  } = useFormBuilderContext();

  const question = getState().fieldMap[condition.questionId];
  if (!question || !('label' in question)) {
    return null;
  }

  const IconForKind = getFieldConfigForKind(question.kind).Icon;

  return {
    question,
    IconForKind,
    elementLabel: getFieldContent(question.label),
    valueDisplay: formatConditionValue(question, condition.value, predefinedOptionsLists),
  };
};

const lookupOptionLabel = (
  question: StateField,
  raw: FormFieldValue,
  predefinedOptionsLists: ReturnType<typeof useFormBuilderContext>['predefinedOptionsLists']
): string => {
  if (!('options' in question)) return String(raw);
  if (question.optionsFromListId) {
    const usedList = predefinedOptionsLists.find(
      (list) => list.value === question.optionsFromListId
    );
    return String(usedList?.options.find((option) => option.value === raw)?.label ?? raw);
  }
  return String(question.options.find((option) => option.value === raw)?.label ?? raw);
};

export const formatConditionValue = (
  question: StateField,
  value: FormFieldValue,
  predefinedOptionsLists: ReturnType<typeof useFormBuilderContext>['predefinedOptionsLists']
): string => {
  const labelled = Array.isArray(value)
    ? value.map((entry) => lookupOptionLabel(question, entry, predefinedOptionsLists)).join(', ')
    : lookupOptionLabel(question, value, predefinedOptionsLists);

  if (question.kind === 'input-date' && isValidDateString(labelled)) {
    return abbreviateDate(labelled, 'en', 'date-fancy');
  }
  return labelled;
};

const ConditionRow = ({
  field,
  condition,
  onSave,
  onRemove,
  onEditJourneyElement,
  shouldGuide,
  onGuideClose,
}: {
  field: StateFieldWithCondition;
  condition: FormFieldCondition;
  onSave(condition: FormFieldCondition): void;
  onRemove(): void;
  onEditJourneyElement(condition: FormFieldConditionJourney): void;
  shouldGuide: boolean;
  onGuideClose(): void;
}) => {
  if (!isFormSourceCondition(condition)) {
    return (
      <JourneyConditionRow
        condition={condition}
        onSave={onSave}
        onRemove={onRemove}
        onEditElement={onEditJourneyElement}
        shouldGuide={shouldGuide}
        onGuideClose={onGuideClose}
      />
    );
  }
  return (
    <FormConditionRow field={field} condition={condition} onSave={onSave} onRemove={onRemove} />
  );
};

const FormConditionRow = ({
  field,
  condition,
  onSave,
  onRemove,
}: {
  field: StateFieldWithCondition;
  condition: FormFieldConditionForm;
  onSave(condition: FormFieldCondition): void;
  onRemove(): void;
}) => {
  const { t } = useScopedTranslate(conditionsLocale);
  const {
    store: { getState },
  } = useFormBuilderContext();
  const display = useFormSourceDisplay(condition);

  if (!display) {
    return null;
  }

  const { IconForKind, elementLabel, valueDisplay } = display;

  const getComparatorsForQuestion = (questionId: FieldId): ReadonlyArray<ConditionComparator> => {
    const question = getState().fieldMap[questionId];
    if (!question) return [];
    return getFieldConfigForKind(question.kind).condition?.comparators ?? [];
  };

  const comparatorLabel = t(`core.comparator.${condition.comparator}`).replace('...', '');
  const isComplete = isCompleteCondition(condition);
  const valueLabel = isComplete && valueDisplay.length > 0 ? valueDisplay : t('selectValue');

  const handleComparatorSelect = (next: ConditionComparator) => {
    onSave(applyComparatorChange(condition, next));
  };

  const handleValueSelect = (next: FormFieldValue) => {
    onSave(applyValueChange(condition, next));
  };

  const handleElementSelect = (nextQuestionId: FieldId) => {
    if (nextQuestionId === condition.questionId) return;
    const nextComparators = getComparatorsForQuestion(nextQuestionId);
    onSave(buildResetFormCondition(condition.id, nextQuestionId, nextComparators));
  };

  const segments: SegmentConfig[] = [
    {
      id: 'element',
      icon: <IconForKind />,
      text: elementLabel,
      tooltip: elementLabel,
      isGrowable: true,
      shouldTruncate: true,
      menuActions: ({ close }) => (
        <ElementPickerMenu
          field={field}
          onSelect={(id) => {
            handleElementSelect(id);
            close();
          }}
        />
      ),
    },
    {
      id: 'comparator',
      text: comparatorLabel,
      textColor: comparatorColor(condition.comparator),
      menuActions: ({ close }) => (
        <ComparatorMenu
          questionId={condition.questionId}
          onSelect={(comparator) => {
            handleComparatorSelect(comparator);
            close();
          }}
        />
      ),
    },
    {
      id: 'selected-value',
      text: valueLabel,
      textColor: isComplete ? 'elementBlue100' : 'textHelper',
      tooltip: isComplete ? valueLabel : undefined,
      isGrowable: true,
      shouldTruncate: true,
      menuWidth: MENU_WIDTH,
      // The value menu can host the inline calendar (date conditions).
      menuStickyPlacement: true,
      menuActions: ({ close }) => (
        <FieldValueMenu
          questionId={condition.questionId}
          comparator={condition.comparator}
          value={condition.value}
          onSelect={(value) => {
            handleValueSelect(value);
            if (!isMultiComparator(condition.comparator)) close();
          }}
        />
      ),
    },
    {
      id: 'close',
      icon: <IconCloseRegular />,
      onClick: onRemove,
    },
  ];

  return (
    <MultiSegmentedFilters segments={segments} shouldWrap containerDataTestid="condition-row" />
  );
};

const AddConditionMenuContent = ({
  field,
  onSave,
  onClose,
  onPickJourneySource,
  hasJourneySource,
}: {
  field: StateFieldWithCondition;
  onSave(condition: FormFieldCondition): void;
  onClose(): void;
  onPickJourneySource(): void;
  hasJourneySource: boolean;
}) => {
  // Without a journey source there is nothing to pick — skip straight to the
  // question list, which is the only-form UX this menu always had.
  const [source, setSource] = useState<ConditionSource | null>(hasJourneySource ? null : 'form');

  const handleSourceSelect = (next: ConditionSource) => {
    if (next === 'journey') {
      onClose();
      onPickJourneySource();
      return;
    }
    setSource(next);
  };

  if (source === null) {
    return <SourcePicker onSelect={handleSourceSelect} />;
  }

  return (
    <QuestionMenu
      field={field}
      onBack={hasJourneySource ? () => setSource(null) : undefined}
      onClose={onClose}
      onSave={onSave}
    />
  );
};

const AddConditionMenu = ({
  field,
  onSave,
  onPickJourneySource,
  hasJourneySource,
}: {
  field: StateFieldWithCondition;
  onSave(condition: FormFieldCondition): void;
  onPickJourneySource(): void;
  hasJourneySource: boolean;
}) => {
  return (
    <Menu
      width={MENU_WIDTH}
      trigger={AddConditionButton}
      placement="bottom-start"
      // Grows from the field list into the inline calendar (date conditions).
      stickyPlacement
    >
      {({ close }) => (
        <AddConditionMenuContent
          field={field}
          onSave={onSave}
          onClose={close}
          onPickJourneySource={onPickJourneySource}
          hasJourneySource={hasJourneySource}
        />
      )}
    </Menu>
  );
};

const AddConditionButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { t } = useScopedTranslate(conditionsLocale);
  return (
    <StyledConditionButton
      {...props}
      variant="secondary"
      iconLeft={<IconAddPlus />}
      iconRight={<IconDropdown />}
      text={t('add')}
      ref={ref}
    />
  );
});

const ConditionsContent = ({ field }: { field: StateFieldWithCondition }) => {
  const { t } = useScopedTranslate(conditionsLocale);
  const {
    store: { actions },
    variableGroups = [],
  } = useFormBuilderContext();
  const state = field.condition;
  const [guidedConditionId, setGuidedConditionId] = useState<string | null>(null);
  const filterableQuestions = useFilterableQuestions(field.id);
  const hasFormSourceAvailable = filterableQuestions.length > 0;
  // Gate on the same filter the picker applies, so a journey whose variables
  // are all condition-incompatible (no comparators for their type) doesn't
  // offer a source that opens an empty sidebar.
  const hasJourneySourceAvailable = journeyVariableGroupsAreNonEmpty(
    filterGroupsToConditionVariables(variableGroups)
  );
  const canAddCondition = hasFormSourceAvailable || hasJourneySourceAvailable;
  const onChange = actions.createChangeHandler(field, 'condition');

  const { open: openJourneyPicker, picker: journeyPicker } = useJourneyVariablePicker(
    variableGroups,
    `condition-${field.id}`
  );

  const updateOperator = (op: ConditionOp) => {
    onChange({ op, conditions: state.conditions });
  };

  const updateCondition = (condition: FormFieldCondition) => {
    onChange({
      op: state.op,
      conditions: state.conditions.map((sc) => {
        if (sc.id === condition.id) return { ...condition };
        return sc;
      }),
    });
  };

  const addCondition = (condition: FormFieldCondition) => {
    onChange({
      op: state.op,
      conditions: [...state.conditions, condition],
    });
  };

  const removeCondition = (condition: FormFieldCondition) => {
    onChange({
      op: state.op,
      conditions: state.conditions.filter((sc) => sc.id !== condition.id),
    });
  };

  // Picking a variable commits the condition right away, then the guided
  // popover walks comparator → value so the user is never left on a bare row.
  const handleAddJourneySource = () => {
    openJourneyPicker((variable) => {
      const id = randomId();
      addCondition(
        buildResetJourneyCondition(id, variable, getComparatorsForFieldType(variable.type))
      );
      setGuidedConditionId(id);
    });
  };

  const handleEditJourneyElement = (condition: FormFieldConditionJourney) => {
    openJourneyPicker((variable) => {
      const current = condition.variable;
      const isSameVariable =
        variable.source === current.source &&
        variable.action === current.action &&
        variable.field === current.field;
      if (isSameVariable) return;
      updateCondition(
        buildResetJourneyCondition(
          condition.id,
          variable,
          getComparatorsForFieldType(variable.type)
        )
      );
    }, condition.variable);
  };

  const operatorOptions: SelectOption[] = [
    { label: t('AND'), value: 'AND' },
    { label: t('OR'), value: 'OR' },
  ];

  return (
    <Stack space={4} style={{ padding: pxToRem(24) }}>
      <Text font="regular14" text={t('description')} />
      <Separator />
      <Stack orientation="horizontal" space={4} align="center" justify="space-between">
        <Text
          font="regular14"
          text={styledTranslate({
            key: 'display',
            translations: conditionsLocale,
            variables: {
              conjunction: {
                text: t(state.op).toLowerCase(),
                font: 'regular14',
                style: { textDecoration: 'underline' },
              },
            },
          })}
        />
        <ConditionsSelectContainer>
          <Select
            value={state.op}
            options={operatorOptions}
            onChange={(op) => updateOperator(op as ConditionOp)}
            isClearable={false}
            isSearchable={false}
          />
        </ConditionsSelectContainer>
      </Stack>
      <Stack space={2} style={{ flexShrink: 1, minWidth: 0 }}>
        {state.conditions.map((condition) => (
          <ConditionRow
            key={condition.id}
            field={field}
            condition={condition}
            onSave={updateCondition}
            onRemove={() => removeCondition(condition)}
            onEditJourneyElement={handleEditJourneyElement}
            shouldGuide={condition.id === guidedConditionId}
            onGuideClose={() => setGuidedConditionId(null)}
          />
        ))}
      </Stack>
      {canAddCondition && state.conditions.length === 0 && (
        <EmptyConditionCard
          header={t('noConditionsHeader')}
          content={t('noConditionsContent')}
          icon={<IconCondition size={48} />}
        />
      )}
      {canAddCondition ? (
        <AddConditionMenu
          field={field}
          onSave={addCondition}
          onPickJourneySource={handleAddJourneySource}
          hasJourneySource={hasJourneySourceAvailable}
        />
      ) : (
        <EmptyContainer>
          <EmptyState
            skipMargin
            variant="small"
            content={t('emptyContent')}
            header={t('emptyHeader')}
            icon={<IconElement size={40} />}
          />
        </EmptyContainer>
      )}
      {hasJourneySourceAvailable && journeyPicker}
    </Stack>
  );
};

const conditionsLocale = {
  en: {
    add: 'Add condition',
    title: 'Conditions',
    display: 'Display this element if {conjunction} of the following matches',
    AND: 'All',
    OR: 'Any',
    emptyContent: 'Conditions can be created from preceding elements with select options',
    emptyHeader: 'No applicable elements',
    noConditionsHeader: 'No conditions yet',
    noConditionsContent: 'Add a condition to control when this element is visible',
    description:
      'Only display this element when specific rules are met, if you want to show or hide multiple elements we recommend putting them inside a section',
    selectValue: 'Select value',
    sourceHeader: 'Condition source',
    sourceForm: 'From current form',
    sourceFormDescription: 'Value in this form',
    sourceJourney: 'From journey workflow',
    sourceJourneyDescription: 'Value from previous action or field in the Journey',
  },
};

export const conditionsTab = (field: StateFieldWithCondition) => {
  const { t } = getScopedTranslate(conditionsLocale);

  return {
    label: t('title'),
    icon: IconCondition,
    tabCount: getRenderableConditionCount(field.condition.conditions),
    content: <ConditionsContent field={field} />,
  };
};

const EmptyContainer = styled.div({
  marginRight: pxToRem(-24),
});

const SourcePickerHeader = styled.div({
  padding: `${pxToRem(4)} ${pxToRem(8)}`,
  font: theme.font.semibold12,
  color: theme.color.textHelper,
  textTransform: 'uppercase',
  letterSpacing: pxToRem(0.5),
});

const EmptyConditionCard = ({
  header,
  content,
  icon,
}: {
  header: string;
  content: string;
  icon: JSX.Element;
}) => (
  <EmptyConditionCardContainer>
    <EmptyConditionCardIcon>{icon}</EmptyConditionCardIcon>
    <Stack space={1}>
      <Text font="semibold14" text={header} />
      <Text font="regular14" color="textHelper" text={content} />
    </Stack>
  </EmptyConditionCardContainer>
);

const EmptyConditionCardContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(16),
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(8),
  padding: pxToRem(16),
});

const EmptyConditionCardIcon = styled.div({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(80),
  height: pxToRem(80),
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.backgroundPrimary,
  '--icon-color': theme.color.elementPurple80,
});

const ConditionsSelectContainer = styled.div({
  flexShrink: 0,
  width: pxToRem(100),
});

// alignSelf keeps the trigger hugging its content now that full-width cards
// (EmptyConditionCard) share the same Stack.
const StyledConditionButton = styled(Button)({
  alignSelf: 'flex-start',
  maxWidth: '100%',
  height: 'auto',
  minHeight: theme.base.height,
  padding: pxToRem(8),
});
